import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useArtists } from '../../contexts/ArtistsContext';

const PageContainer = styled.div`
  max-width: 1120px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h1`
  font-size: var(--font-size-3xl);
  font-family: var(--font-serif);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
`;

const SummaryRow = styled.div`
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  flex-wrap: wrap;
`;

const SummaryBadge = styled.span`
  background: var(--color-surface);
  color: var(--color-accent);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: var(--space-xs) var(--space-sm);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
  justify-content: space-between;
`;

const FiltersGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
`;

const ActionsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  align-items: center;
`;

const SearchInput = styled.input`
  height: 46px;
  padding: var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  min-width: 220px;
  background: var(--color-surface);
  color: var(--color-text);

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    top: calc(50% - 1px);
    right: var(--space-sm);
    width: 8px;
    height: 8px;
    border-right: 2px solid var(--color-text-secondary);
    border-bottom: 2px solid var(--color-text-secondary);
    transform: translateY(-50%) rotate(45deg);
    pointer-events: none;
    transition: border-color var(--transition-speed) ease;
  }

  &:focus-within::after {
    border-color: var(--color-accent);
  }
`;

const StatusSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  height: 46px;
  padding: var(--space-xs) var(--space-xl) var(--space-xs) var(--space-sm);
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  background: white;
  color: var(--color-text);
  cursor: pointer;
  transition: border-color var(--transition-speed) ease, box-shadow var(--transition-speed) ease;

  &:hover {
    border-color: var(--color-accent);
  }

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const RefreshButton = styled.button`
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text-secondary);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  min-height: 36px;
  
  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-primary);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.56;
  }
`;

const PrimaryButton = styled(RefreshButton)`
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: white;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    color: white;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  overflow-x: auto;
`;

const ArtistsTable = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
`;

const TableHeadCell = styled.th`
  text-align: ${(props) => props.$numeric ? 'right' : 'left'};
  padding: var(--space-sm) var(--space-md);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  background: var(--color-primary-lighter);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: rgba(247, 247, 243, 0.72);
  }

  &:hover {
    background: rgba(130, 172, 216, 0.08);
  }
`;

const TableCell = styled.td`
  text-align: ${(props) => props.$numeric ? 'right' : 'left'};
  padding: var(--space-md);
  font-size: var(--font-size-md);
  color: var(--color-text);
  border-bottom: 1px solid var(--color-border-light);
`;

const CountValue = styled.span`
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  font-variant-numeric: tabular-nums;
`;

const ArtistNameLink = styled(Link)`
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;

  &:hover {
    color: var(--color-accent);
  }
`;

const Badge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$tone',
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0;
  white-space: nowrap;
  background: ${(props) => {
    if (props.$tone === 'ready') return 'rgba(130, 172, 216, 0.16)';
    if (props.$tone === 'warning') return 'rgba(180, 120, 24, 0.12)';
    if (props.$tone === 'test') return 'rgba(0, 24, 206, 0.08)';
    return 'rgba(93, 64, 34, 0.06)';
  }};
  color: ${(props) => {
    if (props.$tone === 'ready' || props.$tone === 'test') return 'var(--color-accent)';
    if (props.$tone === 'warning') return 'var(--color-warning)';
    return 'var(--color-text-muted)';
  }};
`;

const EmptyState = styled.div`
  padding: var(--space-xl);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
`;

const ErrorBox = styled.div`
  padding: var(--space-lg);
  border: 1px solid var(--color-error);
  border-radius: var(--border-radius-md);
  background: rgba(180, 35, 24, 0.08);
  color: var(--color-error);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-md);
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
  background: rgba(0, 0, 0, 0.36);
`;

const Modal = styled.div`
  width: min(720px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: white;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  box-shadow: var(--shadow-large);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-lg);
  border-bottom: 1px solid var(--color-border-light);
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--color-primary);
  font-family: var(--font-serif);
  font-size: var(--font-size-xl);
`;

const ModalBody = styled.div`
  display: grid;
  gap: var(--space-md);
  padding: var(--space-lg);
`;

const TextAreaGroup = styled.label`
  display: grid;
  gap: var(--space-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
`;

const EmailTextArea = styled.textarea`
  min-height: 112px;
  resize: vertical;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-md);
  padding: var(--space-sm);
  color: var(--color-text);
  background: var(--color-surface);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--font-size-sm);
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 4px rgba(0, 24, 206, 0.08);
  }
`;

const ModalFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
`;

const CopyStatus = styled.span`
  margin-right: auto;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
`;

const getConnectStatus = (artist) => {
  const connect = artist.stripeConnect || artist.connect || null;

  if (artist.connectReady === true) {
    return { label: 'Ready', category: 'ready', tone: 'ready' };
  }

  const hasConnectAccount = Boolean(artist.stripeConnectAccountId || connect?.accountId);
  if (!hasConnectAccount) {
    return { label: 'Not set up', category: 'not-ready', tone: 'muted' };
  }

  const requirements = [
    ...(connect?.requirementsPastDue || []),
    ...(connect?.requirementsCurrentlyDue || []),
  ];

  if (requirements.length > 0 || connect?.disabledReason) {
    return { label: 'Needs info', category: 'not-ready', tone: 'warning' };
  }

  return { label: 'In progress', category: 'not-ready', tone: 'muted' };
};

const uniqueEmailsForArtists = (artists) => {
  const emails = new Map();

  artists.forEach((artist) => {
    const email = String(artist.user_email || artist.email || '').trim();
    if (!email) return;
    emails.set(email.toLowerCase(), email);
  });

  return Array.from(emails.values());
};

const ExistingArtists = () => {
  const { artists, loadingArtists: loading, error, refreshArtists } = useArtists();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [testFilter, setTestFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [payoutFilter, setPayoutFilter] = useState('all');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  const filteredArtists = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    return artists.filter((artist) => {
      const payoutStatus = getConnectStatus(artist);
      const displayName = artist.user_display_name || '';
      const email = artist.user_email || artist.email || '';
      const matchesSearch =
        normalizedTerm.length === 0 ||
        displayName.toLowerCase().includes(normalizedTerm) ||
        email.toLowerCase().includes(normalizedTerm);

      if (!matchesSearch) {
        return false;
      }

      if (statusFilter === 'active' && !artist.account_active) {
        return false;
      }

      if (statusFilter === 'inactive' && artist.account_active) {
        return false;
      }

      if (testFilter === 'test' && !artist.isTestArtist) {
        return false;
      }

      if (testFilter === 'standard' && artist.isTestArtist) {
        return false;
      }

      if (accountFilter === 'complete' && !artist.profile_completed) {
        return false;
      }

      if (accountFilter === 'incomplete' && artist.profile_completed) {
        return false;
      }

      if (payoutFilter !== 'all' && payoutStatus.category !== payoutFilter) {
        return false;
      }

      return true;
    });
  }, [artists, searchTerm, statusFilter, testFilter, accountFilter, payoutFilter]);

  const filteredEmails = useMemo(() => uniqueEmailsForArtists(filteredArtists), [filteredArtists]);
  const commaSeparatedEmails = filteredEmails.join(', ');
  const filtersActive =
    searchTerm.trim() ||
    statusFilter !== 'all' ||
    testFilter !== 'all' ||
    accountFilter !== 'all' ||
    payoutFilter !== 'all';

  const openEmailModal = () => {
    setCopyStatus('');
    setEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    setCopyStatus('');
    setEmailModalOpen(false);
  };

  const copyEmails = async () => {
    if (!commaSeparatedEmails) {
      setCopyStatus('No emails to copy.');
      return;
    }

    try {
      await navigator.clipboard.writeText(commaSeparatedEmails);
      setCopyStatus('Copied.');
    } catch {
      setCopyStatus('Unable to copy from this browser.');
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <Title>Existing Artists</Title>
        <Subtitle>Review the artists who currently have access to the platform.</Subtitle>
        <SummaryRow>
          <SummaryBadge>{artists.length} total</SummaryBadge>
          {filtersActive && (
            <SummaryBadge>{filteredArtists.length} showing</SummaryBadge>
          )}
          <RefreshButton onClick={refreshArtists} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </RefreshButton>
        </SummaryRow>
        <ControlsRow>
          <FiltersGroup>
            <SearchInput
              type="search"
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search artists by name or email"
            />
            <SelectWrapper>
              <StatusSelect
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter artists by status"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </StatusSelect>
            </SelectWrapper>
            <SelectWrapper>
              <StatusSelect
                value={testFilter}
                onChange={(event) => setTestFilter(event.target.value)}
                aria-label="Filter artists by test group"
              >
                <option value="all">All groups</option>
                <option value="test">Test artists</option>
                <option value="standard">Standard artists</option>
              </StatusSelect>
            </SelectWrapper>
            <SelectWrapper>
              <StatusSelect
                value={accountFilter}
                onChange={(event) => setAccountFilter(event.target.value)}
                aria-label="Filter artists by account completion"
              >
                <option value="all">All onboarding</option>
                <option value="complete">Onboarding complete</option>
                <option value="incomplete">Onboarding incomplete</option>
              </StatusSelect>
            </SelectWrapper>
            <SelectWrapper>
              <StatusSelect
                value={payoutFilter}
                onChange={(event) => setPayoutFilter(event.target.value)}
                aria-label="Filter artists by payout status"
              >
                <option value="all">All payouts</option>
                <option value="ready">Payouts ready</option>
                <option value="not-ready">Payouts not ready</option>
              </StatusSelect>
            </SelectWrapper>
          </FiltersGroup>
          <ActionsGroup>
            <PrimaryButton onClick={openEmailModal} disabled={filteredEmails.length === 0}>
              Export emails
            </PrimaryButton>
          </ActionsGroup>
        </ControlsRow>
      </PageHeader>

      {error ? (
        <ErrorBox role="alert">
          <span>{error}</span>
          <RefreshButton onClick={refreshArtists}>Try again</RefreshButton>
        </ErrorBox>
      ) : (
        <TableWrapper aria-busy={loading}>
          {loading ? (
            <EmptyState>Loading artists...</EmptyState>
          ) : artists.length === 0 ? (
            <EmptyState>No artists found. Invite someone new to get started.</EmptyState>
          ) : filteredArtists.length === 0 ? (
            <EmptyState>No artists match your search or filter.</EmptyState>
          ) : (
            <ArtistsTable>
              <thead>
                <tr>
                  <TableHeadCell scope="col">Name</TableHeadCell>
                  <TableHeadCell scope="col" $numeric>Drafts</TableHeadCell>
                  <TableHeadCell scope="col" $numeric>Published</TableHeadCell>
                  <TableHeadCell scope="col">Test group</TableHeadCell>
                  <TableHeadCell scope="col">Account complete</TableHeadCell>
                  <TableHeadCell scope="col">Payouts</TableHeadCell>
                </tr>
              </thead>
              <tbody>
                {filteredArtists.map((artist) => {
                  const artworkCounts = artist.artwork_counts || {};
                  const payoutStatus = getConnectStatus(artist);

                  return (
                    <TableRow key={artist.id}>
                      <TableCell>
                        <ArtistNameLink to={`/artists/existing/${artist.id}`}>
                          {artist.user_display_name}
                        </ArtistNameLink>
                      </TableCell>
                      <TableCell $numeric>
                        <CountValue>{artworkCounts.drafts ?? 0}</CountValue>
                      </TableCell>
                      <TableCell $numeric>
                        <CountValue>{artworkCounts.published ?? 0}</CountValue>
                      </TableCell>
                      <TableCell>
                        <Badge $tone={artist.isTestArtist ? 'test' : 'muted'}>
                          {artist.isTestArtist ? 'Test artist' : 'Standard'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge $tone={artist.profile_completed ? 'ready' : 'muted'}>
                          {artist.profile_completed ? 'Complete' : 'Incomplete'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge $tone={payoutStatus.tone}>
                          {payoutStatus.label}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </tbody>
            </ArtistsTable>
          )}
        </TableWrapper>
      )}

      {emailModalOpen && (
        <ModalBackdrop onClick={closeEmailModal}>
          <Modal
            role="dialog"
            aria-modal="true"
            aria-labelledby="artist-email-export-title"
            onClick={(event) => event.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle id="artist-email-export-title">
                Filtered artist emails
              </ModalTitle>
              <RefreshButton onClick={closeEmailModal}>Close</RefreshButton>
            </ModalHeader>
            <ModalBody>
              <SummaryBadge>{filteredEmails.length} email{filteredEmails.length === 1 ? '' : 's'}</SummaryBadge>
              <TextAreaGroup>
                Comma separated
                <EmailTextArea readOnly value={commaSeparatedEmails} aria-label="Comma separated artist emails" />
              </TextAreaGroup>
            </ModalBody>
            <ModalFooter>
              <CopyStatus>{copyStatus}</CopyStatus>
              <PrimaryButton onClick={copyEmails} disabled={filteredEmails.length === 0}>
                Copy
              </PrimaryButton>
              <RefreshButton onClick={closeEmailModal}>Done</RefreshButton>
            </ModalFooter>
          </Modal>
        </ModalBackdrop>
      )}
    </PageContainer>
  );
};

export default ExistingArtists;
